/**
 * The SQL box's saved queries and history (DESIGN_EDITING.md E25), as pure functions so the rules are testable on
 * their own. Saved queries live on the platform (one person's, per database); history lives only in this browser,
 * because a statement that was run can carry customer values.
 */

// ── History (browser only) ───────────────────────────────────────────────────────────────────────────────────────

export const HISTORY_MAX = 50;

export interface HistoryEntry { sql: string; at: string }

export const historyKey = (databaseId: number): string => `redu.dataView.history.${databaseId}`;

/** This database's history, newest first. A store that is missing, throws or holds something else reads as empty. */
export const readHistory = (storage: Pick<Storage, 'getItem'> | null | undefined, databaseId: number): HistoryEntry[] => {
    try {
        const raw = storage?.getItem(historyKey(databaseId));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .filter((e: any) => e && typeof e.sql === 'string' && e.sql.trim() !== '' && typeof e.at === 'string')
            .map((e: any) => ({ sql: e.sql, at: e.at }))
            .slice(0, HISTORY_MAX);
    } catch {
        return [];
    }
};

/** The history after a run: the statement on top (a repeat moves up, never twice), at most HISTORY_MAX kept. */
export const addToHistory = (history: HistoryEntry[], sql: string, at: string): HistoryEntry[] => {
    const text = sql.trim();
    if (!text) return history;
    return [{ sql: text, at }, ...history.filter((e) => e.sql !== text)].slice(0, HISTORY_MAX);
};

/** Keeps the history; a store that refuses (full, blocked) only means it is not kept. */
export const writeHistory = (storage: Pick<Storage, 'setItem' | 'removeItem'> | null | undefined, databaseId: number, history: HistoryEntry[]): void => {
    try {
        if (history.length === 0) storage?.removeItem(historyKey(databaseId));
        else storage?.setItem(historyKey(databaseId), JSON.stringify(history));
    } catch {
        // Not kept, nothing else.
    }
};

/** One line for a menu: the statement's first non-empty line, cut at 80 characters. */
export const sqlPreview = (sql: string): string => {
    const first = sql.split('\n').map((l) => l.trim()).find((l) => l !== '') ?? '';
    return first.length > 80 ? `${first.slice(0, 79)}…` : first;
};

// ── Saved queries (the platform) ─────────────────────────────────────────────────────────────────────────────────

export interface SavedQuery { id: number; name: string; sql: string; updated_at: string }

export const savedQueriesUrl = (databaseId: number): string => `/v1/databases/${databaseId}/data/queries`;
export const savedQueryUrl = (databaseId: number, queryId: number): string => `/v1/databases/${databaseId}/data/queries/${queryId}`;

/** The list answer, or null when it is not one (the menu then says it could not be read). */
export const readSavedQueries = (data: unknown): SavedQuery[] | null => {
    const list = (data as any)?.queries;
    if (!Array.isArray(list)) return null;
    const out: SavedQuery[] = [];
    for (const q of list) {
        if (!q || typeof q.id !== 'number' || typeof q.name !== 'string' || typeof q.sql !== 'string') return null;
        out.push({ id: q.id, name: q.name, sql: q.sql, updated_at: typeof q.updated_at === 'string' ? q.updated_at : '' });
    }
    return out;
};

/** One short line for a refused save, rename or delete, in the console's words. */
export const savedQueryErrorLine = (err: unknown): string => {
    const code = (err as any)?.response?.data?.error?.code;
    if (code === 'name_taken') return 'Name taken.';
    if (code === 'too_many_saved') return 'At most 100 saved. Delete one first.';
    if (code === 'query_not_found') return 'Already deleted.';
    if ((err as any)?.response?.status === 400) return (err as any)?.response?.data?.error?.message ?? 'Not saved.';
    return 'Not saved. Try again.';
};
